import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useProfileById } from '@/hooks/useProfile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { useToast } from '@/hooks/use-toast';
import { Lock, ArrowRight } from 'lucide-react';

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [expired, setExpired] = useState(false);
  const { session, clearPasswordRecovery } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfileById(session?.user?.id);
  const navigate = useNavigate();
  const { toast } = useToast();

  const isStrongPassword = (value: string) => {
    if (value.length < 8) return false;
    if (!/[a-z]/.test(value)) return false;
    if (!/[A-Z]/.test(value)) return false;
    if (!/[0-9]/.test(value)) return false;
    return true;
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please make sure both passwords are the same.',
        variant: 'destructive',
      });
      return;
    }

    if (!isStrongPassword(password)) {
      toast({
        title: 'Password too weak',
        description: 'Use at least 8 characters, including upper/lowercase and a number.',
        variant: 'destructive',
      });
      return;
    }

    if (!session) {
      setExpired(true);
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      clearPasswordRecovery();

      toast({
        title: 'Password updated',
        description: 'Welcome to QBN! Redirecting...',
      });

      setTimeout(() => navigate('/home', { replace: true }), 1500);
    } catch (error: any) {
      if (error.message?.includes('session') || error.message?.includes('token') || error.message?.includes('expired')) {
        setExpired(true);
      } else {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex flex-col">
      <header className="p-6">
        <div className="flex items-center gap-3">
          <img src="/qbn-logo.png" alt="QBN" className="w-10 h-10 rounded-lg" />
          <div>
            <h1 className="font-display font-bold text-lg text-white">QBN</h1>
            <p className="text-xs text-white/60">Queer Business Network</p>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Card className="bg-card/95 backdrop-blur border-border">
            {expired ? (
              <>
                <CardHeader>
                  <CardTitle className="text-lg">Link expired</CardTitle>
                  <CardDescription>
                    This reset link has expired or has already been used. Please request a new one.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    className="w-full btn-qbn-primary"
                    onClick={() => navigate('/auth', { replace: true })}
                  >
                    Go to Sign In
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </>
            ) : (
            <>
            <CardHeader className="text-center">
              {session && (
                <div className="flex flex-col items-center gap-3 mb-4">
                  <Avatar className="h-16 w-16">
                    <SignedAvatarImage src={profile?.avatar_url || undefined} />
                    <AvatarFallback className="bg-primary/10 text-primary text-xl">
                      {profileLoading ? '...' : (profile?.full_name?.charAt(0) || 'U')}
                    </AvatarFallback>
                  </Avatar>
                  <p className="text-lg font-medium text-foreground">
                    {profileLoading
                      ? 'Loading...'
                      : `Welcome, ${profile?.full_name?.split(' ')[0] || 'Member'}!`}
                  </p>
                </div>
              )}
              <CardTitle className="text-lg">Set your password</CardTitle>
              <CardDescription>
                Create a password to access your QBN account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="At least 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      minLength={8}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="Re-enter password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      minLength={8}
                      required
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full btn-qbn-primary"
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Set Password & Sign In'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>
            </CardContent>
            </>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
}
